import { Component, Input, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { AdminService, BaseComponent, UserService } from "@mypxplat/xplat/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from "rxjs/operators";

interface SyncResult {
  system: string;
  status: "ok" | "skipped" | "failed";
  detail?: string;
  /** Human label, resolved once on receipt so the template does no work per change detection. */
  label?: string;
}

/**
 * Admin-only modal for changing a customer's email address.
 *
 * The address is a login identifier, so this is deliberately more deliberate than the rest of
 * the customer detail page: the agent confirms the address twice, supplies a reason that is
 * recorded on the profile, and sees a per-system report afterwards rather than a bare success.
 * The change also signs the user out everywhere, which the confirmation copy states up front.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-change-email",
  templateUrl: "change-email.component.html",
  standalone: false,
})
export class ChangeEmailComponent extends BaseComponent {
  @Input() user: any;

  public newEmail: string = "";
  public confirmEmail: string = "";
  public reason: string = "";
  public loading: boolean = false;
  public success: boolean = false;
  public error: string = "";
  public results: SyncResult[] = [];
  public failedResults: SyncResult[] = [];
  /** The address actually submitted, kept so the success panel can echo it back. */
  public submittedEmail: string = "";

  /** Live availability feedback, so a taken address is caught before the agent commits. */
  public checking: boolean = false;
  public availability: { available: boolean; conflicts: { store: string; detail: string }[] } = null;

  private _emailChanged$ = new Subject<string>();

  /** Human labels for the systems the backend reports on. */
  private readonly systemLabels = {
    fender_user_service: "Fender user service",
    mysql: "Customer database",
    fender_cognito: "Fender Cognito",
    presonus_cognito: "PreSonus Cognito",
    fender_users_table: "Fender users table",
    iterable: "Iterable",
    presonus_stripe: "Stripe (PreSonus)",
    fender_play_stripe: "Stripe (Fender Play)",
    zendesk: "Zendesk",
    firebase: "Community profile",
    fender_shopify: "Shopify",
    session_invalidation: "Sign out all sessions",
    audit_note: "Audit note",
  };

  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public router: Router,
    public adminService: AdminService
  ) {
    super(userService);
  }

  ngOnInit() {
    this._emailChanged$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((email) => {
          if (!this.emailIsWellFormed(email)) {
            this.checking = false;
            return of(null);
          }
          this.checking = true;
          return this.adminService.checkEmailAvailability(email).pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((result: any) => {
        this.checking = false;
        this.availability = result;
      });
  }

  emailChanged() {
    this.availability = null;
    this._emailChanged$.next((this.newEmail || "").trim().toLowerCase());
  }

  emailIsWellFormed(email: string): boolean {
    return !!email && this.userService.emailTester.test(email);
  }

  get newEmailIsValid(): boolean {
    return this.emailIsWellFormed((this.newEmail || "").trim());
  }

  get emailsMatch(): boolean {
    return (this.newEmail || "").trim().toLowerCase() === (this.confirmEmail || "").trim().toLowerCase();
  }

  get sameAsCurrent(): boolean {
    return (this.newEmail || "").trim().toLowerCase() === (this.user?.email || "").trim().toLowerCase();
  }

  get canSubmit(): boolean {
    return !this.loading && this.newEmailIsValid && this.emailsMatch && !this.sameAsCurrent && !!(this.reason || "").trim() && this.availability?.available !== false;
  }

  submit() {
    if (!this.canSubmit) return;
    this.loading = true;
    this.error = "";
    const newEmail = (this.newEmail || "").trim().toLowerCase();
    this.adminService
      .changeUserEmail(this.user.id, {
        newEmail,
        reason: (this.reason || "").trim(),
      })
      .subscribe({
        next: (result: any) => {
          this.loading = false;
          this.success = true;
          this.submittedEmail = newEmail;
          this.results = (result?.results || []).map((entry: SyncResult) => ({
            ...entry,
            label: this.systemLabels[entry.system] || entry.system,
          }));
          this.failedResults = this.results.filter((entry) => entry.status === "failed");
        },
        error: (error) => {
          this.loading = false;
          this.error = error?.error?.error || error?.message || "The email change failed. Please try again.";
        },
      });
  }

  /** Closing with `true` tells the customer detail page to refetch. */
  done() {
    this.activeModal.close(this.success);
  }
}
